/**
 * TriBVH — a static triangle BVH built from scratch (no three-mesh-bvh).
 *
 * Layout is flat typed arrays; no per-node objects are ever allocated.
 *
 *   nodeBounds : Float32Array(node * 6)   minX minY minZ maxX maxY maxZ
 *   nodeMeta   : Int32Array(node * 4)     [0] = leftChild | firstTri
 *                                         [1] = triCount  (0 => internal node)
 *                                         [2] = rightChild
 *                                         [3] = split axis (debug/ordering)
 *
 * Triangle data is reordered at the end of the build so every leaf is a
 * contiguous run — leaves iterate straight down a Float32Array.
 *
 *   pos   : Float32Array(tri * 9)  a.xyz b.xyz c.xyz in WORLD space
 *   nrm   : Float32Array(tri * 3)  unit face normal
 *   surf  : Uint8Array(tri)        index into SURFACES
 *   mask  : Uint16Array(tri)       collision mask bits
 *   obj   : Int32Array(tri)        index into this.objects
 *
 * The build is time-sliced: `buildAsync()` yields to the event loop whenever a
 * slice exceeds `sliceMs`, so a 60k-triangle level never blocks boot.
 *
 * owner: physics
 */
import { rayTriangle } from './PhysMath.js';

export const SURFACES = [
  'concrete', 'metal', 'wood', 'dirt', 'sand',
  'glass', 'fabric', 'water', 'flesh', 'foliage',
];
const SURFACE_INDEX = new Map(SURFACES.map((s, i) => [s, i]));
export const surfaceIndex = (name) => SURFACE_INDEX.get(name) ?? 0;

const LEAF_SIZE = 6;
const MAX_DEPTH = 40;
const BINS = 12;

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export class TriBVH {
  constructor() {
    this.triCount = 0;
    this.nodeCount = 0;
    this.pos = new Float32Array(0);
    this.nrm = new Float32Array(0);
    this.surf = new Uint8Array(0);
    this.mask = new Uint16Array(0);
    this.obj = new Int32Array(0);
    this.objects = [];
    this.nodeBounds = new Float32Array(0);
    this.nodeMeta = new Int32Array(0);
    this.bounds = { min: [0, 0, 0], max: [0, 0, 0] };

    // traversal scratch
    this._stack = new Int32Array(128);
    this._cand = new Int32Array(4096);
    this._buildStats = { ms: 0, slices: 0, tris: 0, nodes: 0, maxDepth: 0 };

    // build scratch (freed once the build completes)
    this._triMin = null; this._triMax = null; this._cent = null; this._idx = null;
    this._task = null;
  }

  get stats() { return this._buildStats; }

  /* ---------------------------------------------------------------- *
   * Source gathering
   * ---------------------------------------------------------------- */

  /**
   * Collect world-space triangles from a list of Object3D roots.
   * @param {THREE.Object3D[]} roots
   * @param {number} defaultMask
   */
  setSources(roots, defaultMask = 1) {
    const meshes = [];
    for (const root of roots) {
      if (!root) continue;
      root.updateMatrixWorld(true);
      root.traverse((o) => {
        if (!o.isMesh || !o.geometry) return;
        if (o.userData.noCollide) return;
        const g = o.geometry;
        if (!g.attributes || !g.attributes.position) return;
        meshes.push(o);
      });
    }

    // count triangles first so we allocate exactly once
    let tris = 0;
    for (const m of meshes) {
      const g = m.geometry;
      const count = g.index ? g.index.count : g.attributes.position.count;
      const inst = m.isInstancedMesh ? m.count : 1;
      tris += Math.floor(count / 3) * inst;
    }

    this.triCount = tris;
    this.pos = new Float32Array(tris * 9);
    this.nrm = new Float32Array(tris * 3);
    this.surf = new Uint8Array(tris);
    this.mask = new Uint16Array(tris);
    this.obj = new Int32Array(tris);
    this.objects = meshes;

    const P = this.pos, N = this.nrm;
    let t = 0;

    const im = new Float64Array(16); // scratch instance matrix

    for (let mi = 0; mi < meshes.length; mi++) {
      const mesh = meshes[mi];
      const g = mesh.geometry;
      const posAttr = g.attributes.position;
      const idx = g.index;
      const triN = Math.floor((idx ? idx.count : posAttr.count) / 3);
      const sIdx = surfaceIndex(mesh.userData.surface || mesh.material?.userData?.surface || 'concrete');
      const mMask = mesh.userData.physicsMask ?? defaultMask;
      const instCount = mesh.isInstancedMesh ? mesh.count : 1;

      for (let inst = 0; inst < instCount; inst++) {
        const e = mesh.matrixWorld.elements;
        if (mesh.isInstancedMesh) {
          const ia = mesh.instanceMatrix.array;
          const o = inst * 16;
          // world = matrixWorld * instanceMatrix
          mul16(e, ia, o, im);
        } else {
          for (let k = 0; k < 16; k++) im[k] = e[k];
        }

        for (let i = 0; i < triN; i++) {
          const o = t * 9;
          for (let v = 0; v < 3; v++) {
            const vi = idx ? idx.getX(i * 3 + v) : i * 3 + v;
            const x = posAttr.getX(vi), y = posAttr.getY(vi), z = posAttr.getZ(vi);
            P[o + v * 3] = im[0] * x + im[4] * y + im[8] * z + im[12];
            P[o + v * 3 + 1] = im[1] * x + im[5] * y + im[9] * z + im[13];
            P[o + v * 3 + 2] = im[2] * x + im[6] * y + im[10] * z + im[14];
          }
          const e1x = P[o + 3] - P[o], e1y = P[o + 4] - P[o + 1], e1z = P[o + 5] - P[o + 2];
          const e2x = P[o + 6] - P[o], e2y = P[o + 7] - P[o + 1], e2z = P[o + 8] - P[o + 2];
          let nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
          const l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
          N[t * 3] = nx / l; N[t * 3 + 1] = ny / l; N[t * 3 + 2] = nz / l;
          this.surf[t] = sIdx;
          this.mask[t] = mMask;
          this.obj[t] = mi;
          t++;
        }
      }
    }
    this.triCount = t; // degenerate geometry may reduce this
    this._buildStats.tris = t;
    return t;
  }

  /* ---------------------------------------------------------------- *
   * Build
   * ---------------------------------------------------------------- */

  beginBuild() {
    const n = this.triCount;
    const triMin = this._triMin = new Float32Array(n * 3);
    const triMax = this._triMax = new Float32Array(n * 3);
    const cent = this._cent = new Float32Array(n * 3);
    const idx = this._idx = new Uint32Array(n);
    const P = this.pos;
    for (let i = 0; i < n; i++) {
      const o = i * 9;
      const ax = P[o], ay = P[o + 1], az = P[o + 2];
      const bx = P[o + 3], by = P[o + 4], bz = P[o + 5];
      const cx = P[o + 6], cy = P[o + 7], cz = P[o + 8];
      const mnx = Math.min(ax, bx, cx), mny = Math.min(ay, by, cy), mnz = Math.min(az, bz, cz);
      const mxx = Math.max(ax, bx, cx), mxy = Math.max(ay, by, cy), mxz = Math.max(az, bz, cz);
      triMin[i * 3] = mnx; triMin[i * 3 + 1] = mny; triMin[i * 3 + 2] = mnz;
      triMax[i * 3] = mxx; triMax[i * 3 + 1] = mxy; triMax[i * 3 + 2] = mxz;
      cent[i * 3] = (mnx + mxx) * 0.5;
      cent[i * 3 + 1] = (mny + mxy) * 0.5;
      cent[i * 3 + 2] = (mnz + mxz) * 0.5;
      idx[i] = i;
    }

    const maxNodes = Math.max(4, n * 2 + 8);
    this.nodeBounds = new Float32Array(maxNodes * 6);
    this.nodeMeta = new Int32Array(maxNodes * 4);
    this.nodeCount = n > 0 ? 1 : 0;
    this._buildStats.slices = 0;
    this._buildStats.maxDepth = 0;
    this._task = n > 0 ? [{ start: 0, count: n, depth: 0, node: 0 }] : [];
    this._t0 = now();
  }

  /** Process build tasks until `deadline` (ms timestamp). Returns true when done. */
  stepBuild(deadline) {
    const stack = this._task;
    if (!stack) return true;
    const nb = this.nodeBounds, nm = this.nodeMeta;
    const triMin = this._triMin, triMax = this._triMax, cent = this._cent, idx = this._idx;
    let checkCounter = 0;

    while (stack.length) {
      const task = stack.pop();
      const { start, count, depth, node } = task;
      if (depth > this._buildStats.maxDepth) this._buildStats.maxDepth = depth;

      // node bounds
      let mnx = Infinity, mny = Infinity, mnz = Infinity;
      let mxx = -Infinity, mxy = -Infinity, mxz = -Infinity;
      let cmnx = Infinity, cmny = Infinity, cmnz = Infinity;
      let cmxx = -Infinity, cmxy = -Infinity, cmxz = -Infinity;
      for (let i = start; i < start + count; i++) {
        const t = idx[i], t3 = t * 3;
        if (triMin[t3] < mnx) mnx = triMin[t3];
        if (triMin[t3 + 1] < mny) mny = triMin[t3 + 1];
        if (triMin[t3 + 2] < mnz) mnz = triMin[t3 + 2];
        if (triMax[t3] > mxx) mxx = triMax[t3];
        if (triMax[t3 + 1] > mxy) mxy = triMax[t3 + 1];
        if (triMax[t3 + 2] > mxz) mxz = triMax[t3 + 2];
        if (cent[t3] < cmnx) cmnx = cent[t3];
        if (cent[t3 + 1] < cmny) cmny = cent[t3 + 1];
        if (cent[t3 + 2] < cmnz) cmnz = cent[t3 + 2];
        if (cent[t3] > cmxx) cmxx = cent[t3];
        if (cent[t3 + 1] > cmxy) cmxy = cent[t3 + 1];
        if (cent[t3 + 2] > cmxz) cmxz = cent[t3 + 2];
      }
      const nb6 = node * 6;
      nb[nb6] = mnx; nb[nb6 + 1] = mny; nb[nb6 + 2] = mnz;
      nb[nb6 + 3] = mxx; nb[nb6 + 4] = mxy; nb[nb6 + 5] = mxz;
      if (node === 0) {
        this.bounds.min[0] = mnx; this.bounds.min[1] = mny; this.bounds.min[2] = mnz;
        this.bounds.max[0] = mxx; this.bounds.max[1] = mxy; this.bounds.max[2] = mxz;
      }

      const nm4 = node * 4;
      if (count <= LEAF_SIZE || depth >= MAX_DEPTH) {
        nm[nm4] = start; nm[nm4 + 1] = count; nm[nm4 + 2] = 0; nm[nm4 + 3] = 0;
        continue;
      }

      // choose the axis with the largest centroid extent
      const ex = cmxx - cmnx, ey = cmxy - cmny, ez = cmxz - cmnz;
      let axis = 0, extent = ex;
      if (ey > extent) { axis = 1; extent = ey; }
      if (ez > extent) { axis = 2; extent = ez; }
      if (extent < 1e-7) {
        nm[nm4] = start; nm[nm4 + 1] = count; nm[nm4 + 2] = 0; nm[nm4 + 3] = 0;
        continue;
      }
      const cmin = axis === 0 ? cmnx : axis === 1 ? cmny : cmnz;
      const scale = BINS / extent;

      // --- binned SAH ---
      binCount.fill(0);
      for (let b = 0; b < BINS; b++) {
        binMin[b * 3] = binMin[b * 3 + 1] = binMin[b * 3 + 2] = Infinity;
        binMax[b * 3] = binMax[b * 3 + 1] = binMax[b * 3 + 2] = -Infinity;
      }
      for (let i = start; i < start + count; i++) {
        const t = idx[i], t3 = t * 3;
        let b = ((cent[t3 + axis] - cmin) * scale) | 0;
        if (b >= BINS) b = BINS - 1; else if (b < 0) b = 0;
        binCount[b]++;
        const b3 = b * 3;
        if (triMin[t3] < binMin[b3]) binMin[b3] = triMin[t3];
        if (triMin[t3 + 1] < binMin[b3 + 1]) binMin[b3 + 1] = triMin[t3 + 1];
        if (triMin[t3 + 2] < binMin[b3 + 2]) binMin[b3 + 2] = triMin[t3 + 2];
        if (triMax[t3] > binMax[b3]) binMax[b3] = triMax[t3];
        if (triMax[t3 + 1] > binMax[b3 + 1]) binMax[b3 + 1] = triMax[t3 + 1];
        if (triMax[t3 + 2] > binMax[b3 + 2]) binMax[b3 + 2] = triMax[t3 + 2];
      }

      // left sweep
      let lmnx = Infinity, lmny = Infinity, lmnz = Infinity;
      let lmxx = -Infinity, lmxy = -Infinity, lmxz = -Infinity, lc = 0;
      for (let b = 0; b < BINS - 1; b++) {
        const b3 = b * 3;
        if (binCount[b]) {
          lc += binCount[b];
          if (binMin[b3] < lmnx) lmnx = binMin[b3];
          if (binMin[b3 + 1] < lmny) lmny = binMin[b3 + 1];
          if (binMin[b3 + 2] < lmnz) lmnz = binMin[b3 + 2];
          if (binMax[b3] > lmxx) lmxx = binMax[b3];
          if (binMax[b3 + 1] > lmxy) lmxy = binMax[b3 + 1];
          if (binMax[b3 + 2] > lmxz) lmxz = binMax[b3 + 2];
        }
        leftArea[b] = lc ? surfaceArea(lmxx - lmnx, lmxy - lmny, lmxz - lmnz) : 0;
        leftCount[b] = lc;
      }
      // right sweep + cost
      let rmnx = Infinity, rmny = Infinity, rmnz = Infinity;
      let rmxx = -Infinity, rmxy = -Infinity, rmxz = -Infinity, rc = 0;
      let bestCost = Infinity, bestSplit = -1;
      for (let b = BINS - 1; b >= 1; b--) {
        const b3 = b * 3;
        if (binCount[b]) {
          rc += binCount[b];
          if (binMin[b3] < rmnx) rmnx = binMin[b3];
          if (binMin[b3 + 1] < rmny) rmny = binMin[b3 + 1];
          if (binMin[b3 + 2] < rmnz) rmnz = binMin[b3 + 2];
          if (binMax[b3] > rmxx) rmxx = binMax[b3];
          if (binMax[b3 + 1] > rmxy) rmxy = binMax[b3 + 1];
          if (binMax[b3 + 2] > rmxz) rmxz = binMax[b3 + 2];
        }
        const li = b - 1;
        if (leftCount[li] === 0 || rc === 0) continue;
        const ra = surfaceArea(rmxx - rmnx, rmxy - rmny, rmxz - rmnz);
        const cost = 0.125 + (leftArea[li] * leftCount[li] + ra * rc);
        if (cost < bestCost) { bestCost = cost; bestSplit = b; }
      }

      const parentArea = surfaceArea(mxx - mnx, mxy - mny, mxz - mnz) || 1e-6;
      const leafCost = parentArea * count;
      if (bestSplit < 0 || bestCost >= leafCost) {
        if (count <= LEAF_SIZE * 4) {
          nm[nm4] = start; nm[nm4 + 1] = count; nm[nm4 + 2] = 0; nm[nm4 + 3] = 0;
          continue;
        }
        bestSplit = BINS >> 1; // forced median-ish split for very fat leaves
      }

      // in-place partition
      let lo = start, hi = start + count - 1;
      while (lo <= hi) {
        const t = idx[lo], t3 = t * 3;
        let b = ((cent[t3 + axis] - cmin) * scale) | 0;
        if (b >= BINS) b = BINS - 1; else if (b < 0) b = 0;
        if (b < bestSplit) lo++;
        else { const tmp = idx[lo]; idx[lo] = idx[hi]; idx[hi] = tmp; hi--; }
      }
      let leftN = lo - start;
      if (leftN === 0 || leftN === count) {
        // degenerate — fall back to a median split on the sorted-by-centroid order
        leftN = count >> 1;
        sortRange(idx, cent, axis, start, count);
      }

      const left = this.nodeCount++;
      const right = this.nodeCount++;
      nm[nm4] = left; nm[nm4 + 1] = 0; nm[nm4 + 2] = right; nm[nm4 + 3] = axis;
      stack.push({ start: start + leftN, count: count - leftN, depth: depth + 1, node: right });
      stack.push({ start, count: leftN, depth: depth + 1, node: left });

      if ((++checkCounter & 63) === 0 && now() > deadline) {
        this._buildStats.slices++;
        return false;
      }
    }

    this._finishBuild();
    return true;
  }

  _finishBuild() {
    // reorder triangle data so leaves are contiguous
    const n = this.triCount, idx = this._idx;
    const P = this.pos, N = this.nrm;
    const np = new Float32Array(n * 9);
    const nn = new Float32Array(n * 3);
    const ns = new Uint8Array(n);
    const nmk = new Uint16Array(n);
    const no = new Int32Array(n);
    for (let i = 0; i < n; i++) {
      const s = idx[i];
      np.set(P.subarray(s * 9, s * 9 + 9), i * 9);
      nn.set(N.subarray(s * 3, s * 3 + 3), i * 3);
      ns[i] = this.surf[s]; nmk[i] = this.mask[s]; no[i] = this.obj[s];
    }
    this.pos = np; this.nrm = nn; this.surf = ns; this.mask = nmk; this.obj = no;
    this.nodeBounds = this.nodeBounds.slice(0, Math.max(1, this.nodeCount) * 6);
    this.nodeMeta = this.nodeMeta.slice(0, Math.max(1, this.nodeCount) * 4);
    this._triMin = this._triMax = this._cent = this._idx = null;
    this._task = null;
    this._buildStats.ms = now() - this._t0;
    this._buildStats.nodes = this.nodeCount;
    const need = Math.max(128, (this._buildStats.maxDepth + 4) * 2);
    if (this._stack.length < need) this._stack = new Int32Array(need);
  }

  /** Synchronous build (used by the self-test and by tiny levels). */
  build() {
    this.beginBuild();
    while (!this.stepBuild(Infinity));
  }

  /**
   * Time-sliced build. Yields to the event loop between slices so a large level
   * never blocks boot for more than `sliceMs`.
   */
  async buildAsync(sliceMs = 8) {
    this.beginBuild();
    if (this.triCount === 0) { this._task = null; this._buildStats.ms = 0; return; }
    for (;;) {
      const done = this.stepBuild(now() + sliceMs);
      if (done) return;
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  /* ---------------------------------------------------------------- *
   * Queries
   * ---------------------------------------------------------------- */

  /**
   * Nearest ray hit. Writes into `out` and returns true on hit.
   * out: { t, tri, nx, ny, nz, px, py, pz }
   */
  raycast(ox, oy, oz, dx, dy, dz, far, mask, out) {
    if (this.nodeCount === 0) return false;
    const nb = this.nodeBounds, nm = this.nodeMeta, P = this.pos;
    const ix = 1 / (dx || 1e-20), iy = 1 / (dy || 1e-20), iz = 1 / (dz || 1e-20);
    const stack = this._stack;
    let sp = 0;
    stack[sp++] = 0;
    let best = far, bestTri = -1;

    while (sp > 0) {
      const node = stack[--sp];
      const b = node * 6;
      // slab test with the current best as tmax
      let t0 = (nb[b] - ox) * ix, t1 = (nb[b + 3] - ox) * ix;
      let tmin = t0 < t1 ? t0 : t1, tmax = t0 < t1 ? t1 : t0;
      t0 = (nb[b + 1] - oy) * iy; t1 = (nb[b + 4] - oy) * iy;
      const ymin = t0 < t1 ? t0 : t1, ymax = t0 < t1 ? t1 : t0;
      if (ymin > tmin) tmin = ymin; if (ymax < tmax) tmax = ymax;
      t0 = (nb[b + 2] - oz) * iz; t1 = (nb[b + 5] - oz) * iz;
      const zmin = t0 < t1 ? t0 : t1, zmax = t0 < t1 ? t1 : t0;
      if (zmin > tmin) tmin = zmin; if (zmax < tmax) tmax = zmax;
      if (tmax < 0 || tmin > tmax || tmin > best) continue;

      const m = node * 4;
      const count = nm[m + 1];
      if (count > 0) {
        const start = nm[m];
        for (let i = start; i < start + count; i++) {
          if (mask !== 0xffff && (this.mask[i] & mask) === 0) continue;
          const o = i * 9;
          const t = rayTriangle(
            ox, oy, oz, dx, dy, dz,
            P[o], P[o + 1], P[o + 2], P[o + 3], P[o + 4], P[o + 5], P[o + 6], P[o + 7], P[o + 8],
          );
          if (t >= 0 && t < best) { best = t; bestTri = i; }
        }
      } else {
        const l = nm[m], r = nm[m + 2];
        // near-child-first: descend the child whose slab entry is nearer
        const axis = nm[m + 3];
        const dirNeg = axis === 0 ? dx < 0 : axis === 1 ? dy < 0 : dz < 0;
        if (dirNeg) { stack[sp++] = l; stack[sp++] = r; }
        else { stack[sp++] = r; stack[sp++] = l; }
      }
    }

    if (bestTri < 0) return false;
    out.t = best;
    out.tri = bestTri;
    out.px = ox + dx * best; out.py = oy + dy * best; out.pz = oz + dz * best;
    const n3 = bestTri * 3;
    let nx = this.nrm[n3], ny = this.nrm[n3 + 1], nz = this.nrm[n3 + 2];
    if (nx * dx + ny * dy + nz * dz > 0) { nx = -nx; ny = -ny; nz = -nz; }
    out.nx = nx; out.ny = ny; out.nz = nz;
    return true;
  }

  /** All ray hits, unsorted, appended to `sink` as {t, tri}. Returns count. */
  raycastAll(ox, oy, oz, dx, dy, dz, far, mask, sink) {
    if (this.nodeCount === 0) return 0;
    const nb = this.nodeBounds, nm = this.nodeMeta, P = this.pos;
    const ix = 1 / (dx || 1e-20), iy = 1 / (dy || 1e-20), iz = 1 / (dz || 1e-20);
    const stack = this._stack;
    let sp = 0, n = 0;
    stack[sp++] = 0;
    while (sp > 0) {
      const node = stack[--sp];
      const b = node * 6;
      let t0 = (nb[b] - ox) * ix, t1 = (nb[b + 3] - ox) * ix;
      let tmin = t0 < t1 ? t0 : t1, tmax = t0 < t1 ? t1 : t0;
      t0 = (nb[b + 1] - oy) * iy; t1 = (nb[b + 4] - oy) * iy;
      const ymin = t0 < t1 ? t0 : t1, ymax = t0 < t1 ? t1 : t0;
      if (ymin > tmin) tmin = ymin; if (ymax < tmax) tmax = ymax;
      t0 = (nb[b + 2] - oz) * iz; t1 = (nb[b + 5] - oz) * iz;
      const zmin = t0 < t1 ? t0 : t1, zmax = t0 < t1 ? t1 : t0;
      if (zmin > tmin) tmin = zmin; if (zmax < tmax) tmax = zmax;
      if (tmax < 0 || tmin > tmax || tmin > far) continue;

      const m = node * 4;
      const count = nm[m + 1];
      if (count > 0) {
        const start = nm[m];
        for (let i = start; i < start + count; i++) {
          if (mask !== 0xffff && (this.mask[i] & mask) === 0) continue;
          const o = i * 9;
          const t = rayTriangle(
            ox, oy, oz, dx, dy, dz,
            P[o], P[o + 1], P[o + 2], P[o + 3], P[o + 4], P[o + 5], P[o + 6], P[o + 7], P[o + 8],
          );
          if (t >= 0 && t <= far) {
            if (sink[n]) { sink[n].t = t; sink[n].tri = i; } else sink[n] = { t, tri: i };
            n++;
            if (n >= sink.length) return n;
          }
        }
      } else { stack[sp++] = nm[m]; stack[sp++] = nm[m + 2]; }
    }
    return n;
  }

  /**
   * Gather triangle indices whose bounds overlap the given AABB.
   * Results land in `this._cand`; returns the count.
   */
  queryBox(mnx, mny, mnz, mxx, mxy, mxz, mask) {
    if (this.nodeCount === 0) return 0;
    const nb = this.nodeBounds, nm = this.nodeMeta;
    const stack = this._stack;
    let sp = 0, n = 0;
    let cand = this._cand;
    stack[sp++] = 0;
    while (sp > 0) {
      const node = stack[--sp];
      const b = node * 6;
      if (nb[b] > mxx || nb[b + 3] < mnx
        || nb[b + 1] > mxy || nb[b + 4] < mny
        || nb[b + 2] > mxz || nb[b + 5] < mnz) continue;
      const m = node * 4;
      const count = nm[m + 1];
      if (count > 0) {
        const start = nm[m];
        if (n + count > cand.length) {
          const bigger = new Int32Array(Math.max(cand.length * 2, n + count + 64));
          bigger.set(cand.subarray(0, n));
          cand = this._cand = bigger;
        }
        for (let i = start; i < start + count; i++) {
          if (mask !== 0xffff && (this.mask[i] & mask) === 0) continue;
          const t3 = i * 9;
          // exact triangle-bounds test (leaf bounds are conservative)
          const P = this.pos;
          const amnx = Math.min(P[t3], P[t3 + 3], P[t3 + 6]);
          if (amnx > mxx) continue;
          const amxx = Math.max(P[t3], P[t3 + 3], P[t3 + 6]);
          if (amxx < mnx) continue;
          const amny = Math.min(P[t3 + 1], P[t3 + 4], P[t3 + 7]);
          if (amny > mxy) continue;
          const amxy = Math.max(P[t3 + 1], P[t3 + 4], P[t3 + 7]);
          if (amxy < mny) continue;
          const amnz = Math.min(P[t3 + 2], P[t3 + 5], P[t3 + 8]);
          if (amnz > mxz) continue;
          const amxz = Math.max(P[t3 + 2], P[t3 + 5], P[t3 + 8]);
          if (amxz < mnz) continue;
          cand[n++] = i;
        }
      } else {
        if (sp + 2 >= stack.length) continue;
        stack[sp++] = nm[m]; stack[sp++] = nm[m + 2];
      }
    }
    return n;
  }

  surfaceOf(tri) { return SURFACES[this.surf[tri]] || 'concrete'; }
  objectOf(tri) { return this.objects[this.obj[tri]] || null; }
}

/* ---- build scratch (module scope, single-threaded) ---- */
const binCount = new Int32Array(BINS);
const binMin = new Float32Array(BINS * 3);
const binMax = new Float32Array(BINS * 3);
const leftArea = new Float32Array(BINS);
const leftCount = new Int32Array(BINS);

function surfaceArea(dx, dy, dz) {
  if (dx < 0 || dy < 0 || dz < 0) return 0;
  return 2 * (dx * dy + dy * dz + dz * dx);
}

function sortRange(idx, cent, axis, start, count) {
  const sub = Array.from(idx.subarray(start, start + count));
  sub.sort((a, b) => cent[a * 3 + axis] - cent[b * 3 + axis]);
  for (let i = 0; i < count; i++) idx[start + i] = sub[i];
}

/** out = a * b (4x4 column-major); b is read from offset `bo`. */
function mul16(a, b, bo, out) {
  for (let c = 0; c < 4; c++) {
    const b0 = b[bo + c * 4], b1 = b[bo + c * 4 + 1], b2 = b[bo + c * 4 + 2], b3 = b[bo + c * 4 + 3];
    out[c * 4] = a[0] * b0 + a[4] * b1 + a[8] * b2 + a[12] * b3;
    out[c * 4 + 1] = a[1] * b0 + a[5] * b1 + a[9] * b2 + a[13] * b3;
    out[c * 4 + 2] = a[2] * b0 + a[6] * b1 + a[10] * b2 + a[14] * b3;
    out[c * 4 + 3] = a[3] * b0 + a[7] * b1 + a[11] * b2 + a[15] * b3;
  }
}
